import React from "react";
import { Typography, Box, TextField, makeStyles } from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import { useCommissionContext } from "../../../../contexts/CommissionContext";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
    width: "100%",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      alignItems: "flex-start",
    },
  },
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      marginBottom: 0,
    },
  },
  content: {
    flex: 1,
    [theme.breakpoints.up("md")]: {
      marginRight: theme.spacing(3),
    },
  },
  dateField: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      marginTop: theme.spacing(1),
      minWidth: 200,
    },
  },
}));

/**
 * CutoffDateSetting is a React component that provides a simple form to set the commission cutoff date.
 * The component displays a brief description of the cutoff date and a date picker to select the date.
 * The component uses the `useCommissionContext` hook to access the commission context and the `setCutoffDate` function to update the commission context with the selected date.
 *
 * @returns {React.ReactElement} - A React component that represents the CutoffDateSetting.
 */
export default function CutoffDateSetting() {
  const classes = useStyles();
  const { cutoffDate, setCutoffDate } = useCommissionContext();

  return (
    <Box className={classes.container}>
      <Box className={classes.content}>
        <Box className={classes.iconWrapper}>
          <CalendarTodayIcon style={{ marginRight: 8 }} />
          <Typography variant="h6">Cutoff Date Settings</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Set the date for commission calculations. This date determines which
          transactions are included.
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2">Cutoff Date</Typography>
        <TextField
          type="date"
          value={cutoffDate}
          onChange={e => setCutoffDate(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.dateField}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Box>
  );
}
